import { useRef } from 'react';
import PropTypes from 'prop-types';
import './SkipToMainContent.css';

export const SkipToMainContent = ({ text = 'Skip to main content', targetSelector = 'alc-pagination-bar' }) => {
  const linkRef = useRef(null);

  const handleSkipClick = (e) => {
    e.preventDefault();

    // Scope the search to the nearest section/custom-element ancestor so that when
    // multiple tables exist on the same page (e.g. cameras + streams viewer), we
    // find the pagination bar that belongs to THIS table, not a sibling table.
    const scopeRoot =
      linkRef.current?.closest('section, main, [class*="all-"], gar-all-cameras, gar-all-streams, gar-all-zones, gar-all-appliances')
      ?? document;

    // Helper to re-query the target on each call — React may re-render the
    // pagination component between this click and the setTimeout callback,
    // detaching any NodeList captured earlier and silently failing focus().
    const getTarget = () => scopeRoot.querySelector(targetSelector) ?? document.querySelector(targetSelector);

    const targetElement = getTarget();
    if (!targetElement) return;

    // Use instant scroll so the element is fully in view before focus fires.
    targetElement.scrollIntoView({ behavior: 'instant', block: 'nearest' });

    setTimeout(() => {
      // Re-query in case React replaced the pagination DOM between click and now.
      const freshTarget = getTarget();
      if (!freshTarget) return;

      const focusableElements = freshTarget.querySelectorAll(
        [
          'button:not([disabled])',
          '[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(', ')
      );

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      } else {
        // Only as a last resort, make the container focusable
        // But ensure it doesn't disrupt tab order by removing it after focus
        freshTarget.setAttribute('tabindex', '-1');
        freshTarget.focus();
        setTimeout(() => {
          freshTarget.removeAttribute('tabindex');
        }, 50);
      }
    }, 100);
  };

  return (
    <a
      ref={linkRef}
      href={`#${targetSelector}`}
      className="skip-to-main-content"
      onClick={handleSkipClick}
    >
      {text}
    </a>
  );
};

SkipToMainContent.propTypes = {
  text: PropTypes.string,
  targetSelector: PropTypes.string,
};

